import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const ImpactMetrics = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [counters, setCounters] = useState({
    serviceHours: 0,
    membersServed: 0,
    fundsRaised: 0,
    leadershipPositions: 0
  });

  const sectionRef = useRef(null);

  const metrics = [
    {
      key: 'serviceHours',
      label: 'Total Service Hours',
      value: 30500,
      icon: 'Clock',
      suffix: '+',
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    {
      key: 'membersServed',
      label: 'Lives Impacted',
      value: 60000,
      icon: 'Users',
      suffix: '+',
      color: 'text-accent',
      bgColor: 'bg-accent/10'
    },
    {
      key: 'fundsRaised',
      label: 'Funds Raised (Rs.)',
      value: 3500000,
      icon: 'Banknote',
      prefix: 'Rs. ',
      suffix: '+',
      color: 'text-secondary',
      bgColor: 'bg-secondary/10'
    },
    {
      key: 'leadershipPositions',
      label: 'Young Leaders Empowered',
      value: 300,
      icon: 'Award',
      suffix: '+',
      color: 'text-error',
      bgColor: 'bg-error/10'
    }
  ];


  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    const duration = 2000; // 2 seconds
    const steps = 60;
    const stepDuration = duration / steps;

    metrics.forEach((metric) => {
      let currentStep = 0;
      const increment = metric.value / steps;

      const timer = setInterval(() => {
        currentStep++;
        const currentValue = Math.min(Math.floor(increment * currentStep), metric.value);

        setCounters(prev => ({
          ...prev,
          [metric.key]: currentValue
        }));

        if (currentStep >= steps) {
          clearInterval(timer);
        }
      }, stepDuration);
    });
  }, [isVisible]);

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(0) + 'K';
    }
    return num.toLocaleString();
  };

  return (
    <section ref={sectionRef} className="py-16 lg:py-24 bg-surface">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              Our Impact in Numbers
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Measurable outcomes that demonstrate our commitment to community service and leadership development
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {metrics.map((metric, index) => (
            <motion.div
              key={metric.key}
              initial={{ opacity: 0, y: 30 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-card rounded-xl p-6 card-shadow hover:card-shadow-hover transition-smooth"
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 rounded-lg ${metric.bgColor} flex items-center justify-center`}>
                  <Icon name={metric.icon} size={24} className={metric.color} />
                </div>
                <div className="text-right">
                  <div className={`text-2xl lg:text-3xl font-bold ${metric.color} animate-count-up`}>
                    {metric.prefix || ''}{formatNumber(counters[metric.key])}{metric.suffix || ''}
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-1">
                  {metric.label}
                </h3>
                <div className="w-full bg-border rounded-full h-2">
                  <motion.div
                    className={`h-2 rounded-full ${metric.color.replace('text-', 'bg-')}`}
                    initial={{ width: 0 }}
                    animate={isVisible ? { width: '100%' } : {}}
                    transition={{ duration: 1.5, delay: index * 0.2 }}
                  />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mt-12"
        >
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium">
            <Icon name="TrendingUp" size={16} className="mr-2" />
            Updated as of January 2025
          </div>
        </motion.div> */}
      </div>
    </section>
  );
};

export default ImpactMetrics;